import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { Button } from "@fp/ui/components/base/buttons/button";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import type { AxiosError } from "axios";
import { getGetApiOnboardingProgressQueryKey } from "@fp/admin-api-client/onboarding-progress/onboarding-progress.client";
import axios, { type ApiErrorResponse } from "@/lib/axios";

// Plaid Link types
interface PlaidLinkHandler {
  open: () => void;
  exit: (options?: { force?: boolean }) => void;
  destroy: () => void;
}

interface PlaidSuccessMetadata {
  institution: {
    institution_id: string;
    name: string;
  } | null;
  accounts: Array<{
    id: string;
    name: string;
    mask: string;
    type: string;
    subtype: string;
  }>;
  link_session_id: string;
}

interface PlaidExitMetadata {
  institution: {
    institution_id: string;
    name: string;
  } | null;
  status: string;
  link_session_id: string;
  request_id: string;
}

interface PlaidError {
  error_type: string;
  error_code: string;
  error_message: string;
  display_message: string | null;
}

interface PlaidConfig {
  token: string;
  onSuccess: (publicToken: string, metadata: PlaidSuccessMetadata) => void;
  onExit: (error: PlaidError | null, metadata: PlaidExitMetadata) => void;
  onLoad?: () => void;
  onEvent?: (eventName: string, metadata: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    Plaid?: {
      create: (config: PlaidConfig) => PlaidLinkHandler;
    };
  }
}

interface LinkTokenResponse {
  linkToken: string;
  expiration: string;
}

interface LinkAccountRequest {
  publicToken: string;
  accountIds: string[];
  institutionId: string;
  institutionName: string;
}

export const Route = createFileRoute("/funding/link-account")({
  component: RouteComponent,
});

// Load Plaid Link SDK script
function loadPlaidScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Plaid) {
      resolve();
      return;
    }

    const existingScript = document.getElementById("plaid-link-script");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Plaid script")));
      return;
    }

    const script = document.createElement("script");
    script.id = "plaid-link-script";
    script.src = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Plaid script"));
    document.head.appendChild(script);
  });
}

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLoadingToken, setIsLoadingToken] = useState(true);
  const [isPlaidReady, setIsPlaidReady] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const plaidHandlerRef = useRef<PlaidLinkHandler | null>(null);

  const linkAccountMutation = useMutation({
    mutationFn: async (data: LinkAccountRequest) => {
      const response = await axios.post("/api/admin/funding/link-account", data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["fundingAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
      queryClient.invalidateQueries({ queryKey: getGetApiOnboardingProgressQueryKey() });
      const count = data.length;
      if (count === 0) {
        toast.error("No accounts could be linked. Some account types may not be supported.", { duration: Infinity });
      } else {
        toast.success(count === 1 ? "Bank account linked successfully" : `${count} bank accounts linked successfully`);
      }
      navigate({ to: "/funding" });
    },
    onError: (err: AxiosError<ApiErrorResponse>) => {
      toast.error(err.response?.data?.error || "Failed to link bank account");
    },
  });

  // Initialize Plaid Link when we have a token
  useEffect(() => {
    if (!linkToken || !window.Plaid) return;

    const handler = window.Plaid.create({
      token: linkToken,
      onSuccess: (publicToken, metadata) => {
        if (metadata.accounts.length === 0) {
          toast.error("No account selected");
          return;
        }

        linkAccountMutation.mutate({
          publicToken: publicToken,
          accountIds: metadata.accounts.map((a) => a.id),
          institutionId: metadata.institution?.institution_id || "",
          institutionName: metadata.institution?.name || "Unknown Bank",
        });
      },
      onExit: (error, _metadata) => {
        if (error) {
          toast.error(error.display_message || "Bank connection was cancelled");
        }
      },
      onLoad: () => {
        setIsPlaidReady(true);
      },
    });

    plaidHandlerRef.current = handler;

    return () => {
      handler.destroy();
      plaidHandlerRef.current = null;
    };
  }, [linkToken, linkAccountMutation]);

  const fetchLinkToken = useCallback(async () => {
    setIsLoadingToken(true);
    setTokenError(null);
    setIsPlaidReady(false);

    try {
      await loadPlaidScript();
      const response = await axios.post<LinkTokenResponse>("/api/admin/funding/link-token");
      setLinkToken(response.data.linkToken);
    } catch (err) {
      const axiosError = err as AxiosError<ApiErrorResponse>;
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to initialize bank connection";
      setTokenError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoadingToken(false);
    }
  }, []);

  useEffect(() => {
    fetchLinkToken();
  }, [fetchLinkToken]);

  const handleOpenPlaid = useCallback(() => {
    if (!plaidHandlerRef.current) {
      toast.error("Bank connection is not ready. Please wait or refresh the page.");
      return;
    }
    plaidHandlerRef.current.open();
  }, []);

  if (isLoadingToken) {
    return (
      <AdminLayout isWide>
        <AdminPageHeader
          title="Link Bank Account"
          parent={{ url: "/funding", label: "Funding" }}
        />
        <PageContentContainer>
          <PageLoading />
        </PageContentContainer>
      </AdminLayout>
    );
  }

  const isReady = isPlaidReady && linkToken && !linkAccountMutation.isPending;

  return (
    <AdminLayout isWide>
      <AdminPageHeader
        title="Link Bank Account"
        parent={{ url: "/funding", label: "Funding" }}
      />
      <PageContentContainer>
        <div className="max-w-md mx-auto space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
            <div className="size-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="size-8 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2">Connect Your Bank</h2>
            <p className="text-gray-600 mb-6">
              Securely connect your business bank account to fund your FieldPay account.
              We use Plaid to safely connect to your bank.
            </p>

            {tokenError ? (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{tokenError}</p>
                </div>
                <Button
                  onClick={fetchLinkToken}
                  color="primary"
                  className="w-full"
                >
                  Try Again
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleOpenPlaid}
                color="primary"
                isLoading={linkAccountMutation.isPending}
                isDisabled={!isReady}
                className="w-full"
              >
                {!isPlaidReady ? "Initializing..." : "Connect Bank Account"}
              </Button>
            )}
          </div>

          <div className="text-center text-sm text-gray-500">
            <p>
              Your bank credentials are never stored on our servers.
              Connection is secured by Plaid.
            </p>
          </div>
        </div>
      </PageContentContainer>
    </AdminLayout>
  );
}
